// src/pages/EventLanding.jsx
import { BRAND_NAME } from "@/config/brand";
import { useDemoPage } from "@/lib/useDemoPage";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const EVENT_IMAGES = {
  hero: "/images/events/artkiv-event-hero.png",
  mobile: "/images/events/artkiv-event-mobile.png",
  confidential: "/images/events/artkiv-event-confidential.png",
};

const LIVE_EVENTS = [
  { label: "Frieze London", whenKey: "thisWeek", city: "London" },
  { label: "Paris+ par Art Basel", whenKey: "nextWeek", city: "Paris" },
  { label: "Art Basel Miami Beach", whenKey: "december", city: "Miami" },
];

const AudienceCard = ({ title, children, image, imagePosition = "center" }) => (
  <article className="group overflow-hidden border border-black/10 bg-[#f6f2ec] transition-transform duration-300 hover:-translate-y-1">
    <div className="aspect-[4/3] overflow-hidden bg-[#ddd5ca]">
      <img
        src={image}
        alt=""
        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.025]"
        style={{ objectPosition: imagePosition }}
        loading="lazy"
      />
    </div>
    <div className="p-6 md:p-7">
      <h3 className="mb-3 font-serif text-2xl leading-tight text-[#181512]">{title}</h3>
      <p className="text-sm leading-relaxed text-[#514a42] md:text-base">{children}</p>
    </div>
  </article>
);

export default function EventLanding() {
  useDemoPage("events");
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen flex-col bg-[#f8f5f0] text-[#181512]">
      <div className="w-full bg-[#181512] text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2.5 text-[13px] md:flex-row md:items-center md:justify-between md:px-6 md:text-sm">
          <div className="flex min-w-0 flex-col gap-1 md:flex-row md:items-center md:gap-3">
            <span className="shrink-0 text-white/60">{t("eventLanding.ribbon.onSite")}</span>
            <div className="flex gap-3 overflow-x-auto whitespace-nowrap [-ms-overflow-style:'none'] [scrollbar-width:'none'] [&::-webkit-scrollbar]:hidden">
              {LIVE_EVENTS.map((event, index) => (
                <span key={event.label} className="flex shrink-0 items-center gap-3 font-medium">
                  <span>
                    {event.label} — {t(`eventLanding.events.when.${event.whenKey}`)}
                  </span>
                  {index < LIVE_EVENTS.length - 1 && (
                    <span aria-hidden="true" className="h-1 w-1 rounded-full bg-white/30" />
                  )}
                </span>
              ))}
            </div>
          </div>

          <a
            href="https://instagram.com/artkiv_official"
            target="_blank"
            rel="noreferrer"
            className="shrink-0 text-white/75 underline decoration-white/30 underline-offset-4 transition-colors hover:text-white"
          >
            {t("eventLanding.ribbon.followStories")}
          </a>
        </div>
      </div>

      <main>
        <section className="bg-[#ded6cb]">
          <div className="relative mx-auto min-h-[680px] max-w-[1600px] overflow-hidden md:min-h-[720px]">
            <img
              src={EVENT_IMAGES.hero}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-[61%_center] md:object-center"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-transparent to-black/55 md:bg-gradient-to-r md:from-black/60 md:via-black/20 md:to-transparent" />

            <div className="relative mx-auto flex min-h-[680px] max-w-7xl items-end px-4 pb-10 pt-24 md:min-h-[720px] md:items-center md:px-6 md:py-20">
              <div className="w-full max-w-xl bg-[#f8f5f0]/95 p-6 shadow-2xl backdrop-blur-sm sm:p-8 md:p-10">
                <div className="mb-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#756658]">
                  <span className="h-px w-8 bg-[#756658]" />
                  {BRAND_NAME} · 2026
                </div>
                <h1 className="mb-5 font-serif text-4xl leading-[1.02] sm:text-5xl md:text-6xl">
                  {t("eventLanding.hero.title")}
                </h1>
                <p className="mb-8 max-w-lg text-base leading-relaxed text-[#514a42] md:text-lg">
                  {t("eventLanding.hero.subtitle")}
                </p>

                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Link
                    to="/signup"
                    className="bg-[#181512] px-6 py-3.5 text-center text-sm font-semibold text-white transition-colors hover:bg-[#352f2a]"
                  >
                    {t("eventLanding.hero.freeTrial")}
                  </Link>
                  <a
                    href="#meet"
                    className="border border-[#181512]/30 bg-white/70 px-6 py-3.5 text-center text-sm font-semibold transition-colors hover:border-[#181512] hover:bg-white"
                  >
                    {t("eventLanding.hero.requestMeeting")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 md:px-6 md:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 flex items-end justify-between border-b border-black/15 pb-5 md:mb-12">
              <div>
                <div className="font-serif text-3xl leading-tight md:text-4xl">{BRAND_NAME}</div>
              </div>
              <span className="hidden text-sm text-[#756e66] md:block">01 — 03</span>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-5">
              <AudienceCard
                title={t("eventLanding.audiences.collectors.title")}
                image={EVENT_IMAGES.mobile}
                imagePosition="58% center"
              >
                {t("eventLanding.audiences.collectors.body")}
              </AudienceCard>

              <AudienceCard
                title={t("eventLanding.audiences.families.title")}
                image={EVENT_IMAGES.confidential}
                imagePosition="center"
              >
                {t("eventLanding.audiences.families.body")}
              </AudienceCard>

              <AudienceCard
                title={t("eventLanding.audiences.professionals.title")}
                image={EVENT_IMAGES.hero}
                imagePosition="72% center"
              >
                {t("eventLanding.audiences.professionals.body")}
              </AudienceCard>
            </div>
          </div>
        </section>

        <section id="meet" className="bg-[#201c19] px-4 py-16 text-white md:px-6 md:py-24">
          <div className="mx-auto grid max-w-7xl overflow-hidden border border-white/15 md:grid-cols-[1.05fr_0.95fr]">
            <div className="relative min-h-[320px] md:min-h-[520px]">
              <img
                src={EVENT_IMAGES.confidential}
                alt=""
                className="absolute inset-0 h-full w-full object-cover object-center"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent md:bg-gradient-to-r md:from-transparent md:to-black/15" />
            </div>

            <div className="flex flex-col justify-center bg-[#201c19] p-7 sm:p-10 md:p-14 lg:p-16">
              <span className="mb-5 h-px w-10 bg-[#c7b6a1]" aria-hidden="true" />
              <h2 className="mb-5 font-serif text-3xl leading-tight md:text-5xl">
                {t("eventLanding.meeting.title")}
              </h2>
              <p className="mb-8 max-w-xl text-sm leading-relaxed text-white/70 md:text-base">
                {t("eventLanding.meeting.body")}
              </p>

              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <a
                  href={`mailto:contact@artkiv.com?subject=${encodeURIComponent(
                    t("eventLanding.meeting.emailSubject")
                  )}`}
                  className="bg-white px-6 py-3.5 text-center text-sm font-semibold text-[#181512] transition-colors hover:bg-[#e9e1d7]"
                >
                  {t("eventLanding.meeting.emailCta")}
                </a>
                <a
                  href="https://instagram.com/artkiv_official"
                  target="_blank"
                  rel="noreferrer"
                  className="border border-white/30 px-6 py-3.5 text-center text-sm font-semibold text-white transition-colors hover:border-white hover:bg-white/10"
                >
                  {t("eventLanding.meeting.instagramCta")}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="mt-auto bg-[#12100f] px-4 py-8 text-xs text-white md:px-6 md:text-sm">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 border-t border-white/10 pt-7 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-2 font-serif text-xl text-white">{BRAND_NAME}</div>
            <div className="max-w-md leading-relaxed text-white/50">
              {t("eventLanding.footer.tagline")}
            </div>
          </div>
          <div className="text-white/35">Paris · London · Miami</div>
        </div>
      </footer>
    </div>
  );
}
